import { yupResolver } from "@hookform/resolvers/yup";
import {
	Breadcrumbs,
	Button,
	designTokens,
	ErrorFeedback,
	Flex,
	Grid,
	Input,
	Label,
	Panel,
	PanelBody,
	PanelHeader
} from "@itandi/itandi-bb-ui";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Td } from "@itandi/itandi-bb-ui/Td";
import { Th } from "@itandi/itandi-bb-ui/Th";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import { Tr } from "@itandi/itandi-bb-ui/Tr";
import Link from "next/link";
import { useRouter } from "next/router";
import type React from "react";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { VendorSettingLayout } from "~/components/common/layouts/settings/VendorSettingLayout";
import { VendorLayoutV2 } from "~/components/common/layouts/VendorLayoutV2";
import { buildIndexPath } from "~/hooks/vendors/useConstructionItemSettings";
import type { CompanySetting } from "~/models/CompanySetting";
import type { ConstructionItem } from "~/models/ConstructionItem";
import type { ConstructionItemSettingEdit } from "~/models/ConstructionItemSetting";
import { validationSchemaConstructionItemSettingForm } from "~/models/ConstructionItemSetting";
import { PATH as SETTING_PATH } from "~/pages/vendors/settings";
import { PATH as COMPANY_SETTINGS_PATH } from "~/pages/vendors/settings/company_settings";
import { isSuccess } from "~/utils/api/api_handler";
import { create as createSetting } from "~/utils/api/vendors/ConstructionItemSetting";
import { toThousandSeparator } from "~/utils/CurrencySeparator";
import SettingStyles from "../../SettingStyle.module.scss";

type Props = {
	companyMasterId: number;
	companySetting: CompanySetting;
	constructionItems: ConstructionItem[];
	constructionItemSettings: ConstructionItemSettingEdit[];
};

export const Page: React.FC<Props> = ({
	companyMasterId,
	companySetting,
	constructionItems,
	constructionItemSettings
}: Props) => {
	const router = useRouter();
	const [editing, setEditing] = useState(false);
	const { mutate: globalMutate } = useSWRConfig();

	const {
		watch,
		setValue,
		handleSubmit,
		reset,
		trigger,
		formState: { errors, isDirty }
	} = useForm<{ settings: ConstructionItemSettingEdit[] }>({
		defaultValues: {
			settings: constructionItemSettings ?? []
		},
		mode: "onBlur",
		resolver: yupResolver(validationSchemaConstructionItemSettingForm)
	});
	const values = watch("settings");

	const handleChangeItemSetting = useCallback(
		(itemId: number, inputValue: string): void => {
			const currentValues = [...values];
			const index = currentValues.findIndex(
				(setting) => setting.constructionItemId === itemId
			);
			const parsedValue = Number(inputValue);
			const valueNum =
				inputValue === "" || Number.isNaN(parsedValue) ? null : parsedValue;
			if (index === -1) {
				currentValues.push({
					constructionItemId: itemId,
					unitAmount: valueNum
				});
			} else {
				const itemSetting = currentValues[index];
				const setting = {
					...itemSetting,
					constructionItemId: itemId,
					unitAmount: valueNum
				};
				currentValues.splice(index, 1, setting);
			}
			setValue("settings", currentValues, {
				shouldDirty: true,
				shouldTouch: true
			});
			trigger("settings");
		},
		[values, setValue, trigger]
	);

	const onSubmit = handleSubmit(async (formData): Promise<void> => {
		const result = await createSetting(formData.settings, companyMasterId);
		if (isSuccess(result)) {
			await globalMutate(buildIndexPath);
			await router.push(COMPANY_SETTINGS_PATH);
			toast.success("保存しました。");
		} else {
			toast.error(`エラーが発生しました。${result.message}`);
		}
	});

	return (
		<VendorLayoutV2>
			<VendorSettingLayout>
				<div className={SettingStyles.PcBreadcrumbsWrapper}>
					<Breadcrumbs
						items={[
							{
								as: Link,
								href: COMPANY_SETTINGS_PATH,
								label: "提携中の管理会社一覧"
							},
							{ label: "工事単価一覧" }
						]}
					/>
				</div>
				<div className={SettingStyles.SpBreadcrumbsWrapper}>
					<Breadcrumbs
						items={[
							{ as: Link, href: SETTING_PATH, label: "設定一覧" },
							{
								as: Link,
								href: COMPANY_SETTINGS_PATH,
								label: "提携中の管理会社一覧"
							},
							{ label: "工事単価一覧" }
						]}
					/>
				</div>
				<Panel>
					<PanelHeader
						rightArea={
							editing ? (
								<Flex gap={designTokens["spacing/xxs"]}>
									<Button
										onClick={(): void => {
											setEditing(false);
											reset();
										}}
										variant="secondary"
									>
										キャンセル
									</Button>
									<Button
										disabled={!isDirty}
										onClick={onSubmit}
										variant="primary"
									>
										更新
									</Button>
								</Flex>
							) : (
								<Button
									onClick={(): void => {
										setEditing(true);
									}}
									variant="primary"
								>
									編集
								</Button>
							)
						}
						title={`${companySetting.name} 工事単価一覧`}
					/>
					<PanelBody>
						<Table>
							<Thead>
								<Tr>
									<Th>項目名</Th>
									<Th textAlign="Right" width="7rem">
										単価
									</Th>
								</Tr>
							</Thead>
							<Tbody>
								{constructionItems.map((item) => {
									const index = values.findIndex(
										(setting) => setting.constructionItemId === item.id
									);
									const itemSetting = index === -1 ? undefined : values[index];
									const unitAmount = itemSetting
										? itemSetting.unitAmount
										: item.unitAmount;
									const fieldError =
										index >= 0 ? errors.settings?.[index] : undefined;
									return (
										<Tr key={item.id}>
											<Td>
												<Grid gap={designTokens["spacing/xxs"]}>
													<Label>{item.name}</Label>
													<Label color="subtle" size="s">
														{`初期値: ¥${
															toThousandSeparator(item.unitAmount) ?? ""
														}`}
													</Label>
												</Grid>
											</Td>
											<Td textAlign="Right">
												{editing && item.overridable ? (
													<>
														<Input
															isInvalid={!!fieldError}
															onChange={(e): void => {
																handleChangeItemSetting(
																	item.id,
																	e.target.value
																);
															}}
															placeholder="単価"
															type="number"
															value={unitAmount ?? ""}
														/>
														{fieldError?.unitAmount?.message != null && (
															<ErrorFeedback>
																{fieldError.unitAmount.message}
															</ErrorFeedback>
														)}
													</>
												) : (
													<>
														{unitAmount
															? `¥${toThousandSeparator(unitAmount)}`
															: null}
													</>
												)}
											</Td>
										</Tr>
									);
								})}
							</Tbody>
						</Table>
					</PanelBody>
				</Panel>
			</VendorSettingLayout>
		</VendorLayoutV2>
	);
};
